import {
  CSSProperties,
  DragEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { ScrollSyncPane } from 'react-scroll-sync';
import {
  differenceInCalendarDays,
  isSameDay,
  startOfDay,
  subDays,
} from 'date-fns';
import useAppSelector from 'hooks/useAppSelector';
import { getPropertyUidFromStringType } from 'utils/property/propertyUtils';
import useCalendarBodyMouseEvents from 'pages/virtualizedStackedCalendar/components/selection/useCalendarBodyMouseEvents';
import CalendarContext from '../CalendarContext';
import useGetCalendarData from '../useGetCalendarData';
import useStackedCalendarPropertiesEntries from '../useStackedCalendarPropertiesEntries';
import useStackedCalendarVisibleProperties from '../useStackedCalendarVisibleProperties';
import { stackedCalendarStyleConstants } from '../Calendar.constants';
import {
  computeColumnVirtualizationRange,
  MAX_RENDERED_COLUMNS,
  useColumnVirtualizationOrchestrator,
} from '../useColumnVirtualization';
import ColumnVirtualizationService from '../ColumnVirtualizationService';
import {
  MAX_RENDERED_ROWS,
  useRowVirtualizationOrchestrator,
} from '../useRowVirtualization';
import { StackedCalendarBodyStyled } from './StackedCalendarBody.styles';
import StackedCalendarBodyRow from './StackedCalendarBodyRow';
import StackedCalendarBodySelectionHandler from './StackedCalendarBodySelectionHandler';
import { getSelectionLimit } from './StackedCalendarBodySelection.utils';
import useStackedCalendarBodyUpdateEvents from './useStackedCalendarBodyUpdateEvents';
import useDragAndDropLeadsUnit from './useDragAndDropLeadsUnit';
import useHighlightUpdatedPricing from './days/useHighlightUpdatedPricing';

const StackedCalendarBody = () => {
  const { mode, filter, calendarBodyWrapperRef } = useContext(CalendarContext);
  const visibleDates = useAppSelector(
    (state) => state.stackedCalendar.visibleDates,
  );
  const propertiesEntries = useStackedCalendarPropertiesEntries();
  const visibleProperties = useStackedCalendarVisibleProperties();
  const [dragDropEventHandlers, highlightEventHandlersRow] =
    useDragAndDropLeadsUnit();
  const lastElementHighlighted = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>();
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  // Read fresh in the deferred scroll below — the Today button resets the date window.
  const visibleDatesRef = useRef(visibleDates);
  visibleDatesRef.current = visibleDates;
  useGetCalendarData();

  const cellWidth = stackedCalendarStyleConstants.daysCellWidth[mode];
  const { startIndex, endIndex } = useColumnVirtualizationOrchestrator({
    scrollContainerRef: scrollableRef,
    totalCount: visibleDates.length,
    cellWidth,
  });
  // Until the orchestrator publishes a range, render a bounded window, not every column.
  const isInitialized = endIndex > 0;
  const effectiveStart = isInitialized ? startIndex : 0;
  const effectiveEnd = isInitialized
    ? endIndex
    : Math.min(visibleDates.length, MAX_RENDERED_COLUMNS);
  // Stable slice reference — lets the memoized row bail on prop equality.
  const renderedDates = useMemo(
    () => visibleDates.slice(effectiveStart, effectiveEnd),
    [visibleDates, effectiveStart, effectiveEnd],
  );
  const leftSpacerWidth = effectiveStart * cellWidth;
  const rightSpacerWidth =
    Math.max(0, visibleDates.length - effectiveEnd) * cellWidth;
  // Full column width so the loading panels span the whole horizontal extent, like the rows.
  const rowWidth = visibleDates.length * cellWidth;
  // Column index of the first Saturday, phasing the panels' weekend-tint gradient.
  const firstSatIndex = visibleDates.length
    ? (6 - visibleDates[0].getDay() + 7) % 7
    : 0;
  // Number of leading columns before today, bounding the panels' past-day shade.
  const pastDaysCount = visibleDates.length
    ? Math.min(
        Math.max(differenceInCalendarDays(new Date(), visibleDates[0]), 0),
        visibleDates.length,
      )
    : 0;
  const loadingPanelStyle = {
    width: rowWidth,
    '--first-sat-index': firstSatIndex,
    '--past-days-count': pastDaysCount,
  } as CSSProperties;

  const rowHeight = stackedCalendarStyleConstants.days.height;
  const { startIndex: rowStartIndex, endIndex: rowEndIndex } =
    useRowVirtualizationOrchestrator({
      bodyWrapperRef: calendarBodyWrapperRef,
      totalCount: visibleProperties.length,
      rowHeight,
    });
  // Until the orchestrator publishes a range, render a bounded window, not every row.
  const rowsInitialized = rowEndIndex > 0;
  const effectiveRowStart = rowsInitialized ? rowStartIndex : 0;
  const effectiveRowEnd = rowsInitialized
    ? rowEndIndex
    : Math.min(visibleProperties.length, MAX_RENDERED_ROWS);
  const renderedProperties = visibleProperties.slice(
    effectiveRowStart,
    effectiveRowEnd,
  );
  const topSpacerHeight = effectiveRowStart * rowHeight;
  const bottomSpacerHeight =
    Math.max(0, visibleProperties.length - effectiveRowEnd) * rowHeight;

  const selectionLimitProvider = useCallback(
    // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    (dayDate, propertyUid) =>
      getSelectionLimit(dayDate, propertiesEntries[propertyUid], mode),
    [propertiesEntries, mode],
  );
  const { onMouseDown, onMouseUp } = useCalendarBodyMouseEvents(
    selectionLimitProvider,
  );

  // Stable drag handlers via a latest-value ref — keeps the memoized row's prop equality intact across re-renders.
  const handlersRef = useRef(highlightEventHandlersRow);
  handlersRef.current = highlightEventHandlersRow;
  const stableRowOnDrop = useCallback(() => {
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    handlersRef.current.onDrop(lastElementHighlighted);
  }, []);
  const stableRowOnDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>, propertyUid: string) => {
      handlersRef.current.onDragEnter(
        e,
        propertyUid,
        // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        lastElementHighlighted,
      );
    },
    [],
  );

  useStackedCalendarBodyUpdateEvents();
  useHighlightUpdatedPricing();

  const initialScrollPositionAdjusted = useRef(false);

  const scrollInToDate = (date: Date, delay: number = 1) => {
    const dateToScroll = subDays(startOfDay(date), 1);

    // Target cell may be virtualized out — scrollIntoView won't find it.
    setTimeout(() => {
      initialScrollPositionAdjusted.current = true;

      const currentVisibleDates = visibleDatesRef.current;
      const targetIndex = currentVisibleDates.findIndex((d) =>
        isSameDay(d, dateToScroll),
      );
      if (targetIndex < 0) return;

      const scrollableEl = scrollableRef.current;
      if (!scrollableEl) return;

      const targetScrollLeft = targetIndex * cellWidth;
      const { clientWidth } = scrollableEl;

      // Pre-stage the range so the destination cells exist when the scroll lands.
      if (clientWidth > 0) {
        ColumnVirtualizationService.setRange(
          computeColumnVirtualizationRange({
            scrollLeft: targetScrollLeft,
            clientWidth,
            cellWidth,
            totalCount: currentVisibleDates.length,
          }),
        );
      }

      scrollableEl.scrollLeft = targetScrollLeft;
    }, delay);
  };

  useEffect(() => {
    if (
      !initialScrollPositionAdjusted.current &&
      bodyRef.current &&
      visibleProperties.length &&
      visibleDates.length
    ) {
      const initialDateToScroll = filter?.availableFrom
        ? new Date(filter?.availableFrom)
        : new Date();

      scrollInToDate(initialDateToScroll);
    }

    // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    const handleFilterEvent = (event) => {
      const { date } = event.detail;
      if (!date) return;

      scrollInToDate(date, 100);
    };

    window.addEventListener('scrollToDateEvent', handleFilterEvent);

    return () => {
      window.removeEventListener('scrollToDateEvent', handleFilterEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyRef.current, visibleProperties, visibleDates, cellWidth]);

  return (
    <StackedCalendarBodyStyled $mode={mode}>
      <StackedCalendarBodySelectionHandler />
      {/* This container captures bubbled events - this prevents having multiple listeners for each child */}
      <div
        className="stacked-calendar-body-container"
        role="presentation"
        // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        ref={bodyRef}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        {...dragDropEventHandlers}
      >
        <ScrollSyncPane innerRef={scrollableRef}>
          <div className="stacked-calendar-body-container-scrollable">
            {topSpacerHeight > 0 && (
              <div
                aria-hidden
                className="virtualization-loading-panel"
                style={{
                  ...loadingPanelStyle,
                  flex: `0 0 ${topSpacerHeight}px`,
                }}
              />
            )}
            {renderedProperties.map((uid) => {
              const { propertyUid, isUnitType, isUnit } =
                getPropertyUidFromStringType(uid);
              return (
                <StackedCalendarBodyRow
                  key={`${propertyUid}-row`}
                  propertyUid={propertyUid}
                  isUnitType={isUnitType}
                  isUnit={isUnit}
                  mode={mode}
                  renderedDates={renderedDates}
                  leftSpacerWidth={leftSpacerWidth}
                  rightSpacerWidth={rightSpacerWidth}
                  onDrop={stableRowOnDrop}
                  onDragEnter={stableRowOnDragEnter}
                />
              );
            })}
            {bottomSpacerHeight > 0 && (
              <div
                aria-hidden
                className="virtualization-loading-panel"
                style={{
                  ...loadingPanelStyle,
                  flex: `0 0 ${bottomSpacerHeight}px`,
                }}
              />
            )}
          </div>
        </ScrollSyncPane>
      </div>
    </StackedCalendarBodyStyled>
  );
};

export default StackedCalendarBody;
